import { z } from "zod";
import { ActorType, IsoDateTime, paged, PageQuery, Uuid } from "./common.ts";

/*
 * The timeline (06 `activity_event`, 03 §5, R-10, R-11). Staff see it per customer in the
 * staff area, customers see their own as the history (`GET /api/history`, UC-25).
 */

/** Every activity type (03 §5), plus `ai_key.revealed` for each plaintext reveal (ADR-043, F-86). */
export const ActivityType = z.enum([
  // customer
  "customer.registered",
  "customer.approved",
  "customer.suspended",
  "customer.reinstated",
  "credit_limit.changed",
  "datacycle_instance.linked",
  "datacycle_instance.changed",
  "datacycle_instance.unlinked",
  // user
  "user.joined",
  "user.role_changed",
  "user.removed",
  // order
  "order.placed",
  "order.status_changed",
  "order.invoice_referenced",
  // entitlement
  "entitlement.created",
  "entitlement.activated",
  "entitlement.converted",
  "entitlement.renewed",
  "entitlement.cancelled",
  "entitlement.expired",
  "entitlement.suspended",
  "entitlement.revoked",
  // provisioning
  "provisioning.completed",
  "provisioning.failed",
  "provisioning.removed",
  // keys and limits
  "ai_key.created",
  "ai_key.revoked",
  "ai_key.rotated",
  "ai_key.revealed",
  "monthly_limit.changed",
  // billing; `usage_report.frozen` names who froze a month (AP-B11.2, migration 0016, TL 2026-10-21)
  "usage_report.created",
  "usage_report.frozen",
  // the BackOffice's mail of final reports written, one per report of the month, once a month;
  // the month close looks for it before it writes the mail (BF10 fix of 2026-11-03, migration 0027)
  "usage_report.final_notified",
  "correction.booked",
  "invoice.booked",
]);
export type ActivityType = z.infer<typeof ActivityType>;

/**
 * One activity. The payload carries before, after and the reason, never personal data (D-09);
 * names are looked up for display. The API decides per route which payload fields leave, for
 * example a staff reason stays in the staff area.
 */
export const ActivityEvent = z.object({
  id: Uuid,
  customerId: Uuid,
  type: ActivityType,
  actor: z.object({
    type: ActorType,
    userId: Uuid.nullable(),
    displayName: z.string().nullable(),
  }),
  subject: z.object({
    type: z.string().min(1),
    id: Uuid,
  }),
  payload: z.record(z.string(), z.unknown()).nullable(),
  createdAt: IsoDateTime,
});
export type ActivityEvent = z.infer<typeof ActivityEvent>;

/** `GET /api/history`: page, size and an optional type (UC-25). */
export const HistoryQuery = PageQuery.extend({
  type: ActivityType.optional(),
});
export type HistoryQuery = z.infer<typeof HistoryQuery>;

/**
 * The answer of `GET /api/history`: the own customer's activities, newest first (UC-25). Staff
 * appear as the team: `actor.type` `staff`, `userId` and `displayName` null. The staff's reasons
 * of an approval and a credit limit are left out of the payload; they stay in the staff area
 * (ADR-095 point 8, ADR-117).
 */
export const HistoryResponse = paged(ActivityEvent);
export type HistoryResponse = z.infer<typeof HistoryResponse>;
